import { ChangeEvent, Dispatch, KeyboardEvent, SetStateAction, useRef, useState } from 'react';
import { Crop } from 'react-image-crop';
import { twMerge } from 'tailwind-merge';
import Icon from '../icon/icon';
import Cropper from '../img-cropper/img-cropper';
import { dataURLtoFile, resizeFile } from '../img-cropper/utils';
import { inputSize } from '../input/input';
import 'react-image-crop/dist/ReactCrop.css';
import Text from '../text/text';

export type IFileInput = {
    id?: string;
    name: string;
    placeholder?: string;
    value?: string | number;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    label?: string;
    onClick?(event: MouseEvent): void;
    onChange?(event: ChangeEvent<HTMLInputElement>): void;
    onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void;
    onBlur?(event: FocusEvent): void;
    onFocus?(event: FocusEvent): void;
    handleChange: Dispatch<SetStateAction<File | undefined>>;
    rounded?: 'standard' | 'full';
    required?: boolean;
    size?: inputSize;
    style?: string;
    width?: number;
    height?: number;
    readonly?: boolean;
    avatarPreview: string | undefined;
    setAvatarPreview: (value: string) => void;
    classImg?: string;
    accept?: string;
    isLoading?: boolean;
    imgDefault?: string;
    modalText?: { title: string; text: string };
    aspectCrop?: number;
    inicialAvatarPreview?: string | undefined;
    inicialCorp?: Crop;
};
export function FileInput(props: IFileInput) {
    const hiddenFileInput = useRef<HTMLInputElement | null>(null);
    const [isOpen, setOpen] = useState(false);

    const onChange = async (file: File) => {
        try {
            const resizedImage = await resizeFile(file, props.width, props.height);
            if (resizedImage) {
                props.handleChange(resizedImage);
            } else {
                console.log('File is too large after resizing');
            }
        } catch (err) {
            console.log(err);
        }
    };
    const handleSave = async (dataURL: string) => {
        const imgFile = dataURL ? dataURLtoFile(dataURL, props.name) : null;
        if (imgFile) {
            /*             console.log(imgFile?.size); */
            await onChange(imgFile as File);
            props.setAvatarPreview(dataURL);
        }
        setOpen(false);
    };

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                props.setAvatarPreview(reader?.result?.toString() || '');
                setOpen(true);
            });
            reader.readAsDataURL(e.target.files[0]);
        }
        if (hiddenFileInput.current) {
            hiddenFileInput.current.value = '';
        }
    };
    const classContainerInput = twMerge(
        'w-full  bg-[#fff] flex justify-start items-center p-2 border border-dashed border-gray-300 rounded-lg',
        props.error && 'border-error-200 hover:border-error-200 active:border-error-200 focus:border-error-200',
    );

    return (
        <>
            {isOpen && (
                <Cropper
                    imgSrc={props.avatarPreview || ''}
                    close={() => {
                        setOpen(false);
                        props.setAvatarPreview(props.inicialAvatarPreview || '');
                    }}
                    save={handleSave}
                    title={props.modalText?.title || 'Ajustá la imagen'}
                    text={props.modalText?.text || 'VISTA PREVIA'}
                    aspectCrop={props.aspectCrop}
                    inicialCorp={props.inicialCorp}
                />
            )}
            <div className="flex">
                {props.label && (
                    <label className="mb-2 block text-sm" htmlFor={props.id || props.name}>
                        {props.label}
                    </label>
                )}
                {props.required && <span className="text-red-600">&nbsp; *</span>}
            </div>
            <div className={classContainerInput}>
                <input
                    data-testid="inputFile"
                    id={props.id || props.name}
                    type="file"
                    name={props.name}
                    accept={props.accept || 'image/*'}
                    className="hidden"
                    onChange={onSelectFile}
                    ref={hiddenFileInput}
                />
                {!props.avatarPreview && (
                    <div data-testid="droppableArea">
                        <label htmlFor={props.id || props.name} className="cursor-pointer">
                            <div className="flex flex-row items-center">
                                <div className="flex flex-row ml-3">
                                    <Icon name="ri-image-add-line" size={20} />
                                    {/* <Text >
                                                Arrastre y suelte una imagen aquí
                                            </Text> */}
                                </div>
                                <div>
                                    <Text className="mx-3">{props.placeholder || 'SELECCIONAR IMAGEN'}</Text>
                                </div>
                            </div>
                        </label>
                    </div>
                )}
                {props.avatarPreview && (
                    <div className="flex items-start">
                        <img src={props.avatarPreview || props.imgDefault} alt="imagen" className={props.classImg} />
                        <div className="flex flex-col my-3 gap-1 justify-start items-start ml-1">
                            <button
                                type="button"
                                className="w-[30px] h-[30px] active:bg-gray-50 rounded"
                                onClick={() => {
                                    props.handleChange(undefined);
                                    props.setAvatarPreview('');
                                }}
                                data-test-id="delete-img-button"
                            >
                                <Icon
                                    name="ri-delete-bin-line"
                                    size={20}
                                    className="rounded-md p-0.5 hover:bg-primary-50"
                                />
                            </button>
                            <button
                                type="button"
                                className=" cursor-pointer w-[30px] h-[30px] active:bg-gray-50 rounded"
                                onClick={() => hiddenFileInput.current?.click()}
                                data-test-id="change-img-button"
                            >
                                <Icon
                                    name="ri-pencil-line"
                                    size={20}
                                    className="rounded-md p-0.5 hover:bg-primary-50"
                                />
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {props.error && props.helperText && <small className="text-red-600">{props.helperText}</small>}
        </>
    );
}
